/**
 * Interests — what Omina asks when it has nothing else to go on.
 *
 * A founder who connects her calendar hands over ninety days of where she has
 * been, and curation turns that into signal. A founder who skips it, or whose
 * calendar holds nothing but personal admin, arrives with the product knowing
 * her name and nothing about the rooms she wants. This is the substitute: five
 * questions she can answer in under a minute.
 *
 * It is a **substitute, not an equal**. Curation is a record of what she
 * actually did; this is a record of what she says she wants, and the two are
 * not the same quality of evidence. Everything stored from here is her stated
 * preference and should be treated as the weaker signal the moment real
 * outcomes exist — see `interests.source` on the profile.
 *
 * **One was cut.** An earlier question asked what would make a room worth her
 * evening, and its eight options were buyers, investors, partners, press,
 * hiring, peers, learning, mentors — which is the goal question the path screen
 * asks two steps later, in the same words, with the same axis. Asking it twice
 * did not double the signal; it made the second ask read as the product not
 * having listened. It also fed nothing: the `feeds` line claimed goal weighting
 * and no code ever read it.
 *
 * Three rules shape the shape:
 *
 *  - **Options are data, not strings in a route.** The API validates against
 *    these arrays and the UI renders from them, so a chip on screen is by
 *    construction a chip the server accepts. Adding an answer is editing one
 *    array.
 *  - **Eight options, choose three.** Eight fits a phone without scrolling and
 *    is enough to feel like her answer is in there. Three forces a ranking:
 *    "everything matters" is the answer that carries no information, and an
 *    unlimited multi-select collects it every time.
 *  - **Every question earns its place by feeding something.** Each one maps to
 *    a field the radar or the classifier already reads (`feeds` below). A
 *    question whose answer nothing consumes is a form, and this product does
 *    not ask founders to fill in forms.
 */

import { PERSONAL_INTERESTS, PERSONAL_QUESTION_ID } from "./personalfit";

export interface InterestOption {
  id: string;
  label: string;
}

export interface InterestQuestion {
  id: string;
  /** The question, in her language. */
  prompt: string;
  /** One line under it. Absent where the prompt is already obvious. */
  hint?: string;
  /** What downstream actually reads this — see the third rule above. */
  feeds: string;
  options: InterestOption[];
}

/** How many an answer must carry. Both ends matter: fewer than this and the
 *  ranking is not forced, more and it stops being a ranking. */
export const INTEREST_PICKS = 3;

export const INTEREST_QUESTIONS: InterestQuestion[] = [
  {
    id: "rooms",
    prompt: "Which rooms do you actually like?",
    feeds: "event_types — the format filter on the radar",
    options: [
      { id: "dinners", label: "Small dinners" },
      { id: "meetups", label: "Meetups" },
      { id: "workshops", label: "Workshops" },
      { id: "conferences", label: "Conferences" },
      { id: "pitch", label: "Pitch nights" },
      { id: "panels", label: "Panels and talks" },
      { id: "coworking", label: "Coworking and community" },
      { id: "online", label: "Online events" },
    ],
  },
  {
    /**
     * The markets she sells into — **all of them, in her order.**
     *
     * Three things are deliberate. It is about her buyers, not her own
     * trade: the rooms worth her evening are the ones her ICP gathers in,
     * whatever she herself builds. It takes up to three, because a founder
     * selling to clinics while opening a second front in insurance was
     * being asked which half of her business to be shown. And the answer
     * reaches `profiles.targetIndustry` / `targetIndustries` directly —
     * before that write existed, this question was asked, stored, and then
     * asked again by the blueprint, which is the whole reason the feed
     * greeted every new founder with a red error.
     *
     * The prompt says "interested in" because that is how a founder talks
     * about a market she is entering as well as one she already sells to;
     * the hint is what stops it reading as the topics question two screens
     * later.
     */
    id: "industries",
    prompt: "What industries are you interested in?",
    hint: "The markets you sell into, or want to. Most important first.",
    feeds: "profiles.targetIndustries — the gate on every room — and the scan's context",
    options: [
      { id: "fintech", label: "Fintech" },
      { id: "healthtech", label: "Health" },
      { id: "real_estate", label: "Real estate" },
      { id: "ecommerce", label: "E-commerce" },
      { id: "saas", label: "SaaS" },
      { id: "clinics", label: "Clinics and care" },
      { id: "trades", label: "Trades and field service" },
      { id: "professional", label: "Professional services" },
    ],
  },
  {
    id: "topics",
    prompt: "What kind of topics are you interested in?",
    feeds: "topics — the semantic half of event scoring",
    options: [
      { id: "ai", label: "AI" },
      { id: "growth", label: "Sales and growth" },
      { id: "product", label: "Product and design" },
      { id: "fundraising", label: "Fundraising" },
      { id: "ops", label: "Operations" },
      { id: "climate", label: "Climate" },
      { id: "community", label: "Community building" },
      { id: "storytelling", label: "Storytelling" },
    ],
  },
  {
    id: "shape",
    prompt: "What kind of events would you like to attend?",
    feeds: "event_size_preference and timing — why a founder skips a good fit",
    options: [
      { id: "intimate", label: "Under twenty people" },
      { id: "medium", label: "Twenty to a hundred" },
      { id: "large", label: "Big rooms" },
      { id: "structured", label: "With an agenda" },
      { id: "loose", label: "Loose and social" },
      { id: "mornings", label: "Mornings" },
      { id: "evenings", label: "Evenings" },
      { id: "weekends", label: "Weekends" },
    ],
  },
  {
    // The one question about her rather than the work. The options, their
    // match vocabulary and the card's referral line are one table in
    // `personalfit.ts`, so the chip she taps is by construction the reason a
    // room later surfaces — see that file for how the answer is consumed.
    id: PERSONAL_QUESTION_ID,
    prompt: "And when it is not work?",
    hint: "The rooms you would go to anyway. Skip if none fit.",
    feeds: "personalMatch — the feed's per-founder lift — and DiscoveryContext.personal in the scan",
    options: PERSONAL_INTERESTS.map(({ id, label }) => ({ id, label })),
  },
];

/** Every option id that exists, for validating a write. */
export function interestOptionIds(questionId: string): string[] {
  return INTEREST_QUESTIONS.find((q) => q.id === questionId)?.options.map((o) => o.id) ?? [];
}

/**
 * True when `answers` is a shape the server should store.
 *
 * Checks membership rather than length alone: an id that is not in the set the
 * UI showed is the failure worth catching, because a stored answer that
 * resolves to no label is worse than a rejected write — three months later it
 * is an un-renderable row nobody can explain.
 *
 * Partial answers are allowed. A founder who answers two of four questions
 * has still told Omina more than it knew, and refusing that would be the
 * product holding her work hostage to completeness.
 */
export function validInterestAnswers(answers: Record<string, string[]>): boolean {
  return Object.entries(answers).every(([questionId, picked]) => {
    const allowed = interestOptionIds(questionId);
    if (allowed.length === 0) return false;
    if (picked.length > INTEREST_PICKS) return false;
    return picked.every((id) => allowed.includes(id));
  });
}
